import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { CourseDigital, CourseDigitalFilter } from '@chancenwerk/shared/courses/CourseDigital.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { Subject, Weekday } from '@chancenwerk/enums/enums.js';
import '@chancenwerk/client/design/components/cr-dropdown-selection.js';
import '@chancenwerk/client/design/components/cr-nospace-toggle-content.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import '@chancenwerk/client/design/components/cr-tabs.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { switchMap } from 'rxjs';
import '../components/board-component-course-digital-card.js';
import { BoardDialogEditCourse, CourseProxy } from '../components/dialogs/board-dialog-edit-course.js';

/**
 * @customElement board-page-course-digital
 */
@customElement('board-page-course-digital')
export class BoardPageCourseDigital extends CrPage {
  @state() private readonly _size = new WindowSizeController(this);

  override styles() {
    return [...super.styles(), { ':host': { height: '100%' } }];
  }

  constructor() {
    super();
    this.label = 'digitale Intensivkurse';
    this.description = 'Hier kannst du die digitalen Intensivkurse sehen.';
    this.permission = ChancenPermission.board.VIEW_DIGITAL_COURSES;
    this.stickyHeader = true;
  }

  @state() private readonly _filter = new RXController(this, new CourseDigitalFilter({}));
  @state() private readonly _digitalCourses = new RXController(
    this,
    this._filter.observable.pipe(
      switchMap(f => {
        return CourseDigital.subscribeDigitalCourseBySearch(f)({
          _id: 1,
          name: 1,
          subject: 1,
          jahrgangsstufe: 1,
          weekday: 1,
          changeNotification: 1,
          startTime: 1,
          startDate: 1,
          endTime: 1,
        });
      }),
    ),
  );

  override header() {
    return html`<cr-button
      slot="right"
      mode="action"
      icon="add"
      @click=${async () => {
        await BoardDialogEditCourse.showPopup({
          props: {
            courseProxy: new CourseProxy({ courses: [new CourseDigital({})] }),
            courseType: CourseDigital._class,
            new: true,
          },
        });
      }}
    >
      Kurs hinzufügen
    </cr-button> `;
  }

  override content() {
    if (this._digitalCourses.isLoading && this._digitalCourses.value)
      return html`
        <cr-flex center crossaxis-center>
          <cr-loader></cr-loader>
        </cr-flex>
      `;

    const groups: Record<string, CourseDigital[]> = {
      [Weekday.MONDAY]: [],
      [Weekday.TUESDAY]: [],
      [Weekday.WEDNESDAY]: [],
      [Weekday.THURSDAY]: [],
      [Weekday.FRIDAY]: [],
    };

    this._digitalCourses.value?.forEach(c => {
      const weekday = c.weekday ?? '';
      const array = groups[weekday];
      if (array) {
        array.push(c);
      }
    });

    const small = (this._size.width ?? 0) < 1400;

    return html` <cr-flex flex space="sm" padding="xs">
      ${this._getFilterSection()}
      <cr-grid columns=${small ? '1fr' : '1fr 1fr 1fr 1fr 1fr'} space="sm" ${css({ width: '100%' })}>
        ${Object.keys(groups).map(weekday => {
          return html`<cr-flex space="sm">
            <cr-h3 ${css({ marginTop: small ? this.spacing.md : undefined })}>${weekday}</cr-h3>
            ${repeat(
              groups[weekday] ?? [],
              course => course._id,
              (course, index) =>
                html`<board-component-course-digital-card .course=${course}></board-component-course-digital-card>`,
            )}
          </cr-flex>`;
        })}
      </cr-grid>
    </cr-flex>`;
  }

  private _getFilterSection() {
    return html`
      <cr-card ${css({ width: '100%' })}>
        <cr-nospace-toggle-content .label=${'Filter ' + (this._filter.value.search ?? '')}>
          <cr-flex space="md">
            <cr-dropdown-search-selection
              clearable
              placeholder="Fächer"
              .search=${(search?: string) =>
                Object.values(Subject)
                  .filter(s => s !== Subject.NATURWISSENSCHAFTEN)
                  .filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
              @value-picked=${e => {
                this._filter.value.subjects = e.detail.value ? [e.detail.value] : [];
                this._filter.next(this._filter.value);
              }}
            ></cr-dropdown-search-selection>
            <cr-dropdown-selection
              clearable
              placeholder="Wochentag"
              .value=${this._filter.value.date ? Object.values(Weekday)[this._filter.value.date?.weekday - 1] : null}
              .selectables=${Object.values(Weekday)}
              @value-picked=${(e: ValueEvent<DateTime | null>) => {
                this._filter.value.date = e.detail.value
                  ? DateTime.now()
                      .startOf('week')
                      .plus({ days: Object.values(Weekday).findIndex(w => w === e.detail.value) })
                  : null;
                this._filter.next(this._filter.value);
              }}
            ></cr-dropdown-selection>
            <cr-dropdown-selection
              clearable
              placeholder="Jahrgangsstufe"
              .value=${this._filter.value.jahrgangsstufe}
              .selectables=${[7, 8, 9, 10, 11, 12, 13]}
              @value-picked=${e => {
                this._filter.value.jahrgangsstufe = e.detail.value;
                this._filter.next(this._filter.value);
              }}
            ></cr-dropdown-selection>
          </cr-flex>
        </cr-nospace-toggle-content>
      </cr-card>
    `;
  }
}
